import { DOCUMENT } from "@angular/common";
import { Component, Inject, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import {
  NavigationEnd,
  NavigationError,
  NavigationCancel,
  Router,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Event
} from "@angular/router";
import { RedirectService } from "@core/services/redirect.service";
import { CarWorkshopPendeciesModalComponent } from "@shared/components/car-workshop-pendecies-modal/car-workshop-pendecies-modal.component";
import { MandatoryDataUpdateCarWorkshopDialogComponent } from "@shared/components/mandatory-data-update-car-workshop-dialog/mandatory-data-update-car-workshop-dialog.component";
import { PlatformNewsModalComponent } from "@shared/components/platform-news-modal/platform-news-modal.component";
import { StatusEnum } from "@shared/enums/status.enum";
import { forkJoin, Observable } from "rxjs";
import { Pagination } from "./shared/models/pagination";
import { assetUrl } from "@shared/helper/asset-url";
import { LoadingDrivebService } from "@shared/components/loading-driveb/loading-driveb.service";
import {
  CarWorkshopService,
  DriverAPIService,
  KeycloakHelperService
} from "@core/services";
import { FeatureToggleService } from "@core/services/feature-toggle.service";
import { map } from "rxjs/operators";
import { inject } from "@angular/core";
import { RemoteConfig, fetchAndActivate } from "@angular/fire/remote-config";
import { AppConfigService } from "@core/services/app-config.service";
import { ConfigurationPolicyCompleteDTO } from "@core/models/app-config.model";

@Component({
  selector: "bci-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: false
})
export class AppComponent implements OnInit {
  carWorkshopId: number | null = null;
  carWorkshopWasFilled = false;
  carWorkshopPendeciesModalWasOpened = false;
  driverAccessWasSent = false;

  private readonly newsModalSessionKey = "platform-news-modal-session";
  private newsModalShownInApp = false;

  serviceRequestPendingState: boolean;

  config$: Observable<ConfigurationPolicyCompleteDTO>;
  remoteConfig = inject(RemoteConfig);

  constructor(
    private router: Router,
    private configService: AppConfigService,
    private keycloakHelperService: KeycloakHelperService,
    private carWorkshopService: CarWorkshopService,
    private driverService: DriverAPIService,
    private redirectService: RedirectService,
    private loadingDrivebService: LoadingDrivebService,
    private dialog: MatDialog,
    private featureToggleService: FeatureToggleService,
    @Inject(DOCUMENT) private _document: any
  ) {
    this.router.events.subscribe((event) => {
      this.showHideLoadingWhenLazyLoading(event);
      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationError ||
        event instanceof NavigationCancel
      ) {
        setTimeout(() => {
          this.hideSplashScreen();
        }, 1200);
      }
      fetchAndActivate(this.remoteConfig).catch((error) =>
        console.error("Remote Config fetch failed", error)
      );
      if (event instanceof NavigationEnd) {
        this.openNewsModalIfNeeded();

        if (
          (this.keycloakHelperService.isDriverLogged() ||
            this.keycloakHelperService.isDriverApproverLogged()) &&
          !this.driverAccessWasSent
        ) {
          const id = +this.keycloakHelperService.getPlatformId();
          this.driverService.updateAccess(id).subscribe();
          this.driverAccessWasSent = true;
        }
      }

      if (
        !(event instanceof NavigationEnd) ||
        !this.keycloakHelperService.isCarWorkshopLogged()
      ) {
        return;
      }

      this.carWorkshopId = +this.keycloakHelperService.getCarWorkshopId();

      if (this.router.url.includes("/carworkshop-upload-files")) {
        return;
      }

      forkJoin({
        restrictItemOnMenu: featureToggleService
          .verifyFeatureToggle()
          .pipe(map((res) => res.restrictItemsOnMenu)),
        carWorkshopWasFilled:
          this.carWorkshopService.getServicesProvidedWasFilled(
            this.carWorkshopId
          )
      }).subscribe(({ restrictItemOnMenu, carWorkshopWasFilled }) => {
        const isCarWorkshopUpdateURL = event.url.includes("car-workshops/edit");

        const canOpenMandatoryDataUpdateCarWorkshop =
          !isCarWorkshopUpdateURL &&
          this.carWorkshopId &&
          !carWorkshopWasFilled;

        if (canOpenMandatoryDataUpdateCarWorkshop && !restrictItemOnMenu) {
          this.openMandatoryDataUpdateCarWorkshop();
        }
      });

      if (!this.carWorkshopPendeciesModalWasOpened) {
        forkJoin([
          this.carWorkshopService.getInvoicesPendencies(),
          this.carWorkshopService.getBudgetPendencies(),
          this.carWorkshopService.getPreventiveProposals()
        ]).subscribe((res) => {
          const invoicesPendencies = res[0];
          const budgetPendencies = res[1];
          const preventiveProposals = res[2];

          if (
            invoicesPendencies.totalElements ||
            budgetPendencies.totalElements ||
            preventiveProposals.totalElements
          ) {
            this.openCarWorkshopPendeciesModal(
              invoicesPendencies,
              budgetPendencies,
              preventiveProposals
            );
          }
        });
      }
    });
  }

  hideSplashScreen(): void {
    this._document
      .getElementsByClassName("splash-screen")[0]
      .classList.add("splash-screen-hidden");
  }

  ngOnInit() {
    this.configService.findClientConfiguration().subscribe();

    document.documentElement.style.setProperty(
      "--app-height",
      `${window.innerHeight}px`
    );
    this.config$ = this.configService.getConfig$();
  }

  openMandatoryDataUpdateCarWorkshop() {
    if (this.existOpenDialog()) return;

    this.dialog
      .open(MandatoryDataUpdateCarWorkshopDialogComponent, {
        width: "600px",
        disableClose: true
      })
      .afterClosed()
      .subscribe(() => {
        this.router.navigate([`/car-workshops/edit/${this.carWorkshopId}`], {
          queryParams: { servicesProvidedUnfilled: true }
        });
      });
  }

  openCarWorkshopPendeciesModal(
    invoicesPendencies: Pagination<string[]>,
    budgetPendencies: Pagination<string[]>,
    preventiveProposals: Pagination<string[]>
  ) {
    if (this.existOpenDialog()) return;

    this.carWorkshopPendeciesModalWasOpened = true;
    this.dialog
      .open(CarWorkshopPendeciesModalComponent, {
        data: { invoicesPendencies, budgetPendencies, preventiveProposals },
        width: "800px",
        panelClass: "reschedule-dialog-container"
      })
      .afterClosed()
      .subscribe((pendencyType) => {
        if (!pendencyType) return;

        if (pendencyType === "preventiveProposals") {
          this.router.navigateByUrl("/home");
          return;
        }

        this.cleanAllFiltersServiceRequest();

        const filteredStatus = [];

        if (pendencyType === "invoicesPendencies") {
          filteredStatus.push(StatusEnum.IN_BILLING);
        }

        if (pendencyType === "budgetPendencies") {
          filteredStatus.push(StatusEnum.SCHEDULED);
        }

        localStorage.setItem("srq-listStatusFilter", filteredStatus.toString());
        localStorage.setItem("srq-carWorkshopPendencyFilter", "true");

        this.redirectService.redirectToServiceRequest();
      });
  }

  existOpenDialog() {
    return this.dialog.openDialogs.length > 0;
  }

  cleanAllFiltersServiceRequest() {
    localStorage.removeItem("srq-requestIdFilter");
    localStorage.removeItem("srq-confirmationDateFilter");
    localStorage.removeItem("srq-serviceTypeFilter");
    localStorage.removeItem("srq-listStatusFilter");
    localStorage.removeItem("srq-client");
    localStorage.removeItem("srq-vehicle");
    localStorage.removeItem("srq-vehicleLicensePlate");
    localStorage.removeItem("srq-cityIdFilter");
    localStorage.removeItem("srq-stateIdFilter");
    localStorage.removeItem("srq-country");
    localStorage.removeItem("srq-carWorkshop");
    localStorage.removeItem("srq-backofficePendencyFilter");
    localStorage.removeItem("srq-carWorkshopPendencyFilter");
  }

  private showHideLoadingWhenLazyLoading(event: Event) {
    if (event instanceof RouteConfigLoadStart) {
      this.loadingDrivebService.show();
    }

    if (event instanceof RouteConfigLoadEnd) {
      this.loadingDrivebService.hide();
    }
  }

  private openNewsModalIfNeeded(): void {
    if (!this.isNewsModalRoleAllowed()) return;
    if (this.existOpenDialog()) return;
    if (this.keycloakHelperService.isArgentinian()) return;

    const sessionMarker = this.keycloakHelperService.getSessionMarker();
    const storedSession = sessionStorage.getItem(this.newsModalSessionKey);

    if (sessionMarker && storedSession === sessionMarker) {
      return;
    }

    if (!sessionMarker && this.newsModalShownInApp) {
      return;
    }

    this.dialog
      .open(PlatformNewsModalComponent, {
        width: "520px"
      })
      .afterClosed()
      .subscribe(() => {
        if (sessionMarker) {
          sessionStorage.setItem(this.newsModalSessionKey, sessionMarker);
          return;
        }

        this.newsModalShownInApp = true;
      });
  }

  private isNewsModalRoleAllowed(): boolean {
    return (
      this.keycloakHelperService.isFleetAdminLogged() ||
      this.keycloakHelperService.isDriverLogged() ||
      this.keycloakHelperService.isDriverApproverLogged()
    );
  }
}
